import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TaskViewer, Spinner } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getCompanyTask, getCompanyDocuments } from 'src/api/company';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/company_/$groupId/tasks/$taskId')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		companyId: (search.companyId as string) || '',
	}),
});

function RouteComponent() {
	const { groupId, taskId } = Route.useParams();
	const navigate = useNavigate();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();

	const { data: task, isLoading: taskLoading } = useQuery({
		queryKey: ['company-task', groupId, taskId],
		queryFn: () => getCompanyTask(groupId, taskId),
		enabled: !userLoading,
	});

	const { data: documentsResponse, isLoading: docsLoading } = useQuery({
		queryKey: ['company-documents', groupId],
		queryFn: () => getCompanyDocuments(groupId),
		enabled: !userLoading,
	});

	const documents = documentsResponse?.data || [];

	useBreadcrumbLevel(1, 'Tasks', `/company/${groupId}/tasks`);
	useBreadcrumbLevel(2, 'Task Details');

	if (userLoading || taskLoading || docsLoading) {
		return null;
	}

	if (!authenticatedUser || !task) return null;

	const verifyPermission = getVerifyPermission(authenticatedUser.permissions);

	// Set read-only
	task.executePermission = {
		isAllowed: task.status === 'todo',
		denyReason: task.status === 'todo' ? undefined : 'You are viewing this task in read-only mode',
	};

	const handleUpdate = () => {
		queryClient.invalidateQueries({ queryKey: ['company-task', groupId, taskId] });
		queryClient.invalidateQueries({ queryKey: ['company-tasks', groupId] });
	};

	return (
		<TaskViewer
			task={task}
			documents={documents || []}
			onUpdate={handleUpdate}
			onClose={() => navigate({ to: '/company/$groupId/tasks', params: { groupId } })}
			onOpenDocument={(documentId) => window.open(`/groups/${groupId}/documents/${documentId}`)}
			permissions={{
				taskDo: verifyPermission(PERMISSIONS.companyApprovalsTasksDo),
				taskDelete: undefined,
			}}
		/>
	);
}
